import React, { useEffect, useState } from "react";
import { EditCatalogueModal } from "../../pages";
import * as yup from "yup";
import { MimaToastUtil } from "../../components";
import { FashCreativesController } from "../../controllers";
import constant from "../../utils/constant";
import {
  colorsArray,
  handleImageUpload,
  removeEmptyKeys,
} from "../../utils/utils";
import { fashCreativeStore } from "../../stores";

const EditCatalogueModalContainer = ({ closeModal }) => {
  const [loading, setLoading] = useState(false);
  const [hashTags, setHashTags] = useState([]);
  const [newCollections, setNewCollections] = useState([]);
  const [filterQuery, setFilterQuery] = useState("");

  useEffect(() => {
    const fetchCollections = async () => {
      if (filterQuery) {
        setLoading(true);
        await Promise.all([
          FashCreativesController.getCollections(filterQuery),
        ]);
        setLoading(false);
      }
    };
    fetchCollections();
  }, [filterQuery]);

  const catalogue = fashCreativeStore.singleCatalogue;

  useEffect(() => {
    // setPrevImageAdded(catalogue?.imageUrls);
    const fetchCollections = async () => {
      setLoading(true);
      await FashCreativesController.getCollections(catalogue?.business?._id);
      setLoading(false);
    };
    if (catalogue?.business?._id) {
      fetchCollections();
    }
    if (catalogue?.style?.styleCollections?.length > 0) {
      setNewCollections(catalogue.style.styleCollections);
    }
  }, [catalogue]);

  const validationSchema = () => {
    return yup.object({
      gender: yup.string().required("Gender is required"),
      isMigration: yup.boolean().required(),
      designerName: yup.string().when("isMigration", {
        is: true,
        then: yup.string().required("Select a Mima user"),
        otherwise: yup.string(),
      }),
      createCollections: yup.boolean().required(),
      styleCollections: yup.array().when(["createCollections", "isMigration"], {
        is: (createCollections, isMigration) =>
          !createCollections && isMigration,
        then: yup
          .array()
          .min(1, "Style Collection is required")
          .required("Style Collection is required"),
        otherwise: yup.array(),
      }),
      local: yup.object().when("isMigration", {
        is: true,
        then: yup.object({
          withoutMaterial: yup.number("Enter Amount"),
          withMaterial: yup.number("Enter Amount"),
          onSaleSlashPrice: yup.number("Enter Amount"),
          currencyCode: yup.string("Select Currency"),
        }),
        otherwise: yup.object(),
      }),
      international: yup.object().when("isMigration", {
        is: true,
        then: yup.object({
          withMaterial: yup.number("Enter Amount"),
          onSaleSlashPrice: yup.number("Enter Amount"),
          currencyCode: yup.string("Select Currency"),
        }),
        otherwise: yup.object(),
      }),

      daysRequiredForDelivery: yup.object({
        early: yup.number().when("$isMigration", {
          is: true,
          then: yup
            .number()
            .required("Early delivery days are required")
            .min(1, "Early delivery days must be at least 1"),
          otherwise: yup.number().nullable(),
        }),
        late: yup.number().when("$isMigration", {
          is: true,
          then: yup
            .number()
            .required("Late delivery days are required")
            .min(
              yup.ref("early"),
              "Late delivery days must be greater than or equal to early delivery days"
            ),
          otherwise: yup.number().nullable(),
        }),
      }),
      addCustomColor: yup.boolean().required(),
      customColors: yup.array().when("addCustomColor", {
        is: true,
        then: yup
          .array()
          .min(1, "Custom color is required")
          .required("Custom color is required"),
        otherwise: yup.array(),
      }),
    });
  };

  const onSubmit = async (values) => {
    const filteredColors = colorsArray
      .filter((color) => values.colorsAvailable.includes(color.code))
      .map((color) => ({
        color: color.color,
        code: color.code,
        type: "PRIMARY",
      }));

    const customColors = values.customColors.map((color) => ({
      color: color,
      code: "",
      type: "CUSTOM",
    }));

    if (values.files?.length < 1) {
      return MimaToastUtil.error({
        message: "Kindly Add atleast 1 image ",
      });
    }

    // if (hashTags?.length < 1) {
    //   return MimaToastUtil.error({
    //     message: "Kindly Add at least 1 hash tag",
    //   });
    // }

    if (
      newCollections?.length < 1 &&
      values.styleCollections.length < 1 &&
      values.isMigration
    ) {
      return MimaToastUtil.error({
        message: "Kindly Add at least 1 collection",
      });
    }

    setLoading(true);
    const imagesToUpload = values.files?.filter((x) => !x.originalUrl);
    const oldImageUrls = values.files
      ?.filter((x) => x.originalUrl)
      .map((z) => z.originalUrl);

    let newImageUrls = [];

    if (imagesToUpload.length > 0) {
      newImageUrls = await handleImageUpload(imagesToUpload);
      if (newImageUrls.length < 1 || newImageUrls === constant.Failed) {
        setLoading(false);
        return MimaToastUtil.error({
          message: "Image upload failed, try again",
        });
      }
    }

    const imageUrls = [...oldImageUrls, ...newImageUrls];

    const payload = {
      ...values,
      hashTags: hashTags,
      imageUrls: imageUrls,
      videoUrls: values.videoUrls.map((item) => item.url),
      colorsAvailable: [...filteredColors, ...customColors],
    };

    delete payload.tradingName;
    delete payload.addLocalSellingPrice;
    delete payload.sellAbroad;
    delete payload.addDeliveryDuration;
    delete payload.createCollections;
    delete payload.isMigration;
    delete payload.files;
    delete payload.addCustomColor;
    delete payload.customColors;

    if (payload.styleCollections.length < 1) {
      payload.styleCollections = newCollections;
    }

    if (!values.isMigration) {
      delete payload.local;
      delete payload.daysRequiredForDelivery;
      delete payload.international;
    }

    removeEmptyKeys(payload);

    const { status, errorMessage } =
      await FashCreativesController.editCatalogue(payload, catalogue._id);

    if (status === constant.Success) {
      MimaToastUtil.success({ message: "Style successfully updated" });
      setLoading(false);
      return closeModal();
    } else {
      setLoading(false);
      MimaToastUtil.error({ message: errorMessage });
    }
  };

  return (
    <EditCatalogueModal
      closeModal={closeModal}
      loading={loading}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      hashTags={hashTags}
      setHashTags={setHashTags}
      newCollections={newCollections}
      setNewCollections={setNewCollections}
      setFilterQuery={setFilterQuery}
    />
  );
};

export default EditCatalogueModalContainer;
